import { exitsThroughGate } from './free-move';
import { occupiedCells } from './shape';
/**
 * Module 75 (D1) — a powered gate whose own block opens it.
 *
 * A powered gate is meant to stay shut until ANOTHER block holds its plate
 * (Module 63's anchor puzzle). Power counts where a block stands right now, and
 * that rule is shared by the model, solver and renderer on purpose. So if the
 * plate sits on a cell the gate's own block covers while it is leaving, that
 * block lights its own gate and walks out, and the shutter, trace and lock badge
 * all mean nothing.
 *
 * "Leaving" is defined by `exitsThroughGate` itself, not restated here: every
 * in-grid anchor for each block is tried and kept when the exit rule accepts
 * it. That is what makes an L, a 1×3's tail and an arrow lock come out right
 * with no special case. Three inputs are pinned so only geometry is asked:
 *  - `others` is empty. Whether a neighbour happens to be in the way is a fact
 *    about one moment of play, not about where the plate may go.
 *  - `surge: true`. Otherwise the power clause would refuse every position and
 *    hide the geometry this is asking about.
 *  - no `rainbow`, and the gate's authored span (not a Widener span). Both are
 *    paid modes; a plate reachable only under one of them is a real plate.
 *
 * Kept out of `power.ts` because `free-move.ts` already imports that file, and
 * this one needs `exitsThroughGate`.
 */
const key = (c) => `${c.row},${c.col}`;
/**
 * Every cell some block of this board covers at a position from which it
 * leaves through `exit`. A plate on any of these cells opens its own gate.
 */
export function selfOpeningCells(exit, blocks, gridSize) {
    const cells = new Set();
    const none = new Set();
    for (const block of blocks) {
        const offsets = occupiedCells({ ...block, position: { row: 0, col: 0 } });
        const maxR = Math.max(...offsets.map((o) => o.row));
        const maxC = Math.max(...offsets.map((o) => o.col));
        for (let row = 0; row + maxR < gridSize; row++)
            for (let col = 0; col + maxC < gridSize; col++) {
                const placed = { ...block, position: { row, col } };
                if (!exitsThroughGate(placed, exit, gridSize, none, { surge: true }))
                    continue;
                for (const cell of occupiedCells(placed))
                    cells.add(key(cell));
            }
    }
    return cells;
}
/** Does `plate`, wired to `exit`, get opened by the gate's own block leaving? */
export function selfOpeningGate(exit, plate, blocks, gridSize) {
    return selfOpeningCells(exit, blocks, gridSize).has(key(plate.cell));
}
